/* Isolated API client for the Task Management system.
   Uses its own token (localStorage: tasks_token) — does NOT share
   credentials with the rental/joinus side. */

import axios from "axios";

const TOKEN_KEY  = "tasks_token";
const USER_KEY   = "tasks_user";

export const TasksAPI = {
	baseUrl() {
		// Dedicated endpoint for the task-management controller
		return window.task_api || "https://bestphonekosher.com/api_tasks/";
	},

	getToken() { return localStorage.getItem(TOKEN_KEY) || ""; },
	setToken(t) {
		if (t) localStorage.setItem(TOKEN_KEY, t);
		else   localStorage.removeItem(TOKEN_KEY);
	},

	getUser() {
		try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
		catch (_) { return null; }
	},
	setUser(u) {
		if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
		else   localStorage.removeItem(USER_KEY);
	},

	clear() { this.setToken(""); this.setUser(null); },

	async call(action, payload = {}, method = "post") {
		const headers = { "Content-Type": "application/json; charset=utf-8" };
		const token = this.getToken();
		if (token) headers["X-Tasks-Token"] = token;

		const url = this.baseUrl() + action;
		try {
			let resp;
			if (method === "get") {
				const params = new URLSearchParams(payload).toString();
				resp = await axios.get(url + (params ? "?" + params : ""), { headers });
			} else {
				resp = await axios.post(url, payload, { headers });
			}
			const data = resp.data && resp.data.data !== undefined ? resp.data.data : resp.data;
			if (data && data.error === "unauthorized") {
				this.clear();
				const base = process.env.BASE_URL || "/";
				if (location.pathname.indexOf(base + "tasks") === 0 && location.pathname !== base + "tasks/login") {
					location.href = base + "tasks/login";
				}
			}
			return data;
		} catch (e) {
			if (e.response && e.response.status === 401) {
				this.clear();
				const base = process.env.BASE_URL || "/";
				if (location.pathname.indexOf(base + "tasks") === 0 && location.pathname !== base + "tasks/login") {
					location.href = base + "tasks/login";
				}
				return { error: "unauthorized" };
			}
			// Try to surface server-returned error body (e.g. 400 with {error:...})
			if (e.response && e.response.data) {
				const body = e.response.data;
				if (body && body.data !== undefined) return body.data;
				return body;
			}
			return { error: "network", message: e.message };
		}
	},

	// auth
	login    (email, password)      { return this.call("tasks_login",    { email, password }); },
	register (name, email, password){ return this.call("tasks_register", { name, email, password }); },
	logout   ()                     { return this.call("tasks_logout"); },
	me       ()                     { return this.call("tasks_me", {}, "get"); },
	forgotPassword(email)           { return this.call("tasks_forgot_password", { email }); },
	resetPassword(token, password)  { return this.call("tasks_reset_password",  { token, password }); },

	// projects
	projects    ()           { return this.call("tasks_projects", {}, "get"); },
	projectGet  (id)         { return this.call("tasks_project_get", { id }, "get"); },
	projectSave (project)    { return this.call("tasks_project_save", project); },
	projectDelete(id)        { return this.call("tasks_project_delete", { id }); },

	// users
	users       ()           { return this.call("tasks_users", {}, "get"); },

	// tasks
	tasksList   (filters={}) { return this.call("tasks_list", filters, "get"); },
	taskGet     (id)         { return this.call("tasks_get", { id }, "get"); },
	taskSave    (task)       { return this.call("tasks_save", task); },
	taskStatus  (id, status) { return this.call("tasks_status", { id, status }); },
	taskAssign  (id, assignee_id) { return this.call("tasks_assign", { id, assignee_id }); },
	taskDelete  (id)         { return this.call("tasks_delete", { id }); },

	// comments
	commentAdd  (task_id, content) { return this.call("tasks_comment_add", { task_id, content }); },
	commentDelete(id)        { return this.call("tasks_comment_delete", { id }); },

	// dashboard
	dashboard   ()           { return this.call("tasks_dashboard", {}, "get"); },

	// notifications
	notifications      ()    { return this.call("tasks_notifications", {}, "get"); },
	notificationsCount ()    { return this.call("tasks_notifications_count", {}, "get"); },
	notificationRead   (id)  { return this.call("tasks_notifications_read", { id }); },
	notificationsReadAll()   { return this.call("tasks_notifications_read", { all: 1 }); },
	notificationDelete (id)  { return this.call("tasks_notifications_delete", { id }); },
	notificationsClearAll()  { return this.call("tasks_notifications_delete", { all: 1 }); },
};

/* -------- Pusher realtime (lazy-loaded, multi-subscriber) ----------
 * Multiple components can subscribe to the same channel. Each subscribe()
 * returns a token; unsubscribe(token) removes only that listener.
 * The actual Pusher channel is unsubscribed only when its refcount hits 0.
 * ------------------------------------------------------------------ */
let pusherInstance = null;
let pusherLoadingPromise = null;
const channels = {}; // name -> { ch, refs: Map<token, handlers> }
let tokenCounter = 0;

export const TasksRealtime = {
	config: {
		key: "939adb5fd45565765e82",
		cluster: "ap1",
	},

	async ensure() {
		if (pusherInstance) return pusherInstance;
		if (pusherLoadingPromise) return pusherLoadingPromise;
		pusherLoadingPromise = (async () => {
			if (!window.Pusher) {
				await new Promise((resolve, reject) => {
					const s = document.createElement("script");
					s.src = "https://js.pusher.com/8.0/pusher.min.js";
					s.async = true;
					s.onload = resolve;
					s.onerror = reject;
					document.head.appendChild(s);
				});
			}
			pusherInstance = new window.Pusher(this.config.key, { cluster: this.config.cluster, forceTLS: true });
			return pusherInstance;
		})();
		return pusherLoadingPromise;
	},

	async subscribe(channelName, handlers) {
		const p = await this.ensure();
		let entry = channels[channelName];
		if (!entry) {
			const ch = p.subscribe(channelName);
			entry = { ch, refs: new Map(), dispatchers: {} };
			channels[channelName] = entry;
		}
		const token = ++tokenCounter;
		entry.refs.set(token, handlers);

		// Ensure one dispatcher per event that fans out to all subscribers
		Object.keys(handlers).forEach((event) => {
			if (entry.dispatchers[event]) return;
			const dispatcher = (data) => {
				entry.refs.forEach((h) => {
					const fn = h[event];
					if (typeof fn === "function") {
						try { fn(data); } catch (e) { /* swallow */ }
					}
				});
			};
			entry.dispatchers[event] = dispatcher;
			entry.ch.bind(event, dispatcher);
		});

		return { channel: channelName, token };
	},

	unsubscribe(handleOrName) {
		if (!handleOrName) return;
		// Back-compat: accept plain channel name
		if (typeof handleOrName === "string") {
			const entry = channels[handleOrName];
			if (!entry) return;
			entry.refs.clear();
			Object.keys(entry.dispatchers).forEach((ev) => entry.ch.unbind(ev, entry.dispatchers[ev]));
			if (pusherInstance) pusherInstance.unsubscribe(handleOrName);
			delete channels[handleOrName];
			return;
		}
		const { channel, token } = handleOrName;
		const entry = channels[channel];
		if (!entry) return;
		entry.refs.delete(token);
		if (entry.refs.size === 0) {
			Object.keys(entry.dispatchers).forEach((ev) => entry.ch.unbind(ev, entry.dispatchers[ev]));
			if (pusherInstance) pusherInstance.unsubscribe(channel);
			delete channels[channel];
		}
	},

	disconnect() {
		Object.keys(channels).forEach((name) => {
			const entry = channels[name];
			Object.keys(entry.dispatchers).forEach((ev) => entry.ch.unbind(ev, entry.dispatchers[ev]));
			if (pusherInstance) pusherInstance.unsubscribe(name);
			delete channels[name];
		});
		if (pusherInstance) {
			pusherInstance.disconnect();
			pusherInstance = null;
		}
		pusherLoadingPromise = null;
	},
};

export const initials = (name) => {
	if (!name) return "?";
	const parts = String(name).trim().split(/\s+/);
	return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

export const formatDate = (d) => {
	if (!d) return "";
	const dt = new Date(d.replace(" ", "T"));
	if (isNaN(dt.getTime())) return d;
	const pad = (n) => String(n).padStart(2, "0");
	return `${pad(dt.getDate())}/${pad(dt.getMonth()+1)}/${dt.getFullYear()}`;
};

export const formatDateTime = (d) => {
	if (!d) return "";
	const dt = new Date(d.replace(" ", "T"));
	if (isNaN(dt.getTime())) return d;
	const pad = (n) => String(n).padStart(2, "0");
	return `${pad(dt.getDate())}/${pad(dt.getMonth()+1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

export const isOverdue = (deadline, status) => {
	if (!deadline || status === "done" || status === "cancelled") return false;
	return new Date(deadline.replace(" ", "T")) < new Date();
};
